import { useEffect, useState, type ReactNode } from "react";
import { Modal } from "./Modal";
import { Button } from "./Button";
import { Field } from "./Field";

export interface InputDialogProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  label: string;
  /** Initial field value (e.g. the current name when renaming). */
  defaultValue?: string;
  confirmLabel?: string;
  /** Optional validator — return an error string to block submit. */
  validate?: (value: string) => string | null;
  /** Async submit; the dialog shows a spinner and closes on success. */
  onSubmit: (value: string) => void | Promise<void>;
}

/** Drop-in replacement for `window.prompt` — a single-field modal form. */
export function InputDialog({
  open,
  onClose,
  title,
  label,
  defaultValue = "",
  confirmLabel = "Save",
  validate,
  onSubmit,
}: InputDialogProps) {
  const [value, setValue] = useState(defaultValue);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setValue(defaultValue);
      setError(null);
    }
  }, [open, defaultValue]);

  const submit = async () => {
    const trimmed = value.trim();
    const err = !trimmed ? `${label} is required` : validate?.(trimmed) ?? null;
    if (err) {
      setError(err);
      return;
    }
    setBusy(true);
    try {
      await onSubmit(trimmed);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={busy ? () => undefined : onClose}
      title={title}
      size="sm"
      staticBehavior={busy}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} disabled={busy} shine>
            {busy ? "Saving…" : confirmLabel}
          </Button>
        </>
      }
    >
      <Field
        label={label}
        value={value}
        autoFocus
        onChange={(e) => {
          setValue(e.target.value);
          if (error) setError(null);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            void submit();
          }
        }}
        error={error ?? undefined}
      />
    </Modal>
  );
}
